import React, { useState } from 'react';
import {
  Alert, Autocomplete, Box, Button, Checkbox, FormControlLabel, Stack, Switch, TextField,
} from '@mui/material';
import { SwitchMode } from '../../../types/backend';
import type { FeatureSwitchMove, SetFeatureSwitchRequest, SwitchState } from '../../../types/backend';
import {
  BACKGROUND_LABEL,
  ENABLED_FOR_OPTIONS,
  formFromState,
  groupOptions,
  sameState,
  stateFromForm,
  validateSwitchForm,
  type EnabledFor,
  type SwitchForm,
} from './featureSwitchForm';

interface Props {
  name: string;
  current: SwitchState;
  availableGroups: string[];
  onSet: (request: SetFeatureSwitchRequest) => Promise<FeatureSwitchMove>;
  onSaved: () => void;
}

/** The switch itself: on or off, and whether it is enabled for selected staff groups (with or without
 * background work) or everyone. */
const FeatureSwitchEditor: React.FC<Props> = ({ name, current, availableGroups, onSet, onSaved }) => {
  const [form, setForm] = useState<SwitchForm>(() => formFromState(current));
  const [hasAttemptedSave, setHasAttemptedSave] = useState(false);
  const [saving, setSaving] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);
  const errors = hasAttemptedSave ? validateSwitchForm(form) : {};
  const id = (field: string) => `switch-${name}-${field}`;
  const update = (change: Partial<SwitchForm>) => setForm((f) => ({ ...f, ...change }));
  const wanted = stateFromForm(form);
  const unchanged = sameState(wanted, current);

  const save = async () => {
    setHasAttemptedSave(true);
    if (Object.keys(validateSwitchForm(form)).length > 0) return;
    setSaving(true);
    setServerError(null);
    try {
      await onSet({
        switch: name, mode: wanted.mode, groups: wanted.groups, background: wanted.background, note: form.note.trim(),
      });
      onSaved();
    } catch (e) {
      setServerError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ mt: 1.5 }}>
      <Stack spacing={1.5}>
        <FormControlLabel
          label="Enabled"
          control={<Switch checked={form.enabled} onChange={(e) => update({ enabled: e.target.checked })} />}
        />
        {form.enabled && (
          <TextField
            select
            size="small"
            id={id('enabled-for')}
            label="Enabled for"
            value={form.enabledFor}
            onChange={(e) => update({ enabledFor: e.target.value as EnabledFor })}
            slotProps={{ select: { native: true }, inputLabel: { shrink: true } }}
          >
            {ENABLED_FOR_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </TextField>
        )}
        {form.enabled && form.enabledFor === SwitchMode.GROUPS && (
          <Autocomplete
            multiple
            size="small"
            id={id('groups')}
            options={groupOptions(availableGroups, current)}
            value={form.groups}
            onChange={(_, picked) => update({ groups: picked })}
            disableCloseOnSelect
            renderInput={(params) => (
              <TextField
                {...params}
                label="Groups"
                placeholder={form.groups.length === 0 ? 'Pick staff groups' : undefined}
                error={!!errors.groups}
                helperText={errors.groups}
              />
            )}
          />
        )}
        {form.enabled && form.enabledFor === SwitchMode.GROUPS && (
          <FormControlLabel
            label={BACKGROUND_LABEL}
            control={(
              <Checkbox
                checked={form.background}
                onChange={(e) => update({ background: e.target.checked })}
              />
            )}
          />
        )}
        <TextField
          size="small"
          id={id('note')}
          label="Note"
          multiline
          value={form.note}
          onChange={(e) => update({ note: e.target.value })}
          slotProps={{ htmlInput: { maxLength: 500 } }}
        />
        {serverError && <Alert severity="error">{serverError}</Alert>}
        <Box>
          <Button size="small" variant="contained" onClick={save} disabled={saving || unchanged}>Save</Button>
        </Box>
      </Stack>
    </Box>
  );
};

export default FeatureSwitchEditor;
